import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { changePassword } from "../services/apiService";

function PasswordToggle({ visible, onToggle, ariaLabel }) {
  return (
    <button
      type="button"
      className="auth-password-toggle"
      onClick={onToggle}
      aria-label={ariaLabel}
    >
      {visible ? (
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z"
            stroke="currentColor"
            strokeWidth="1.5"
          />
          <circle
            cx="12"
            cy="12"
            r="3"
            stroke="currentColor"
            strokeWidth="1.5"
          />
        </svg>
      ) : (
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M3 3l18 18"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
          <path
            d="M10.58 10.58A3 3 0 0 0 12 15a3 3 0 0 0 2.42-4.42M9.88 4.24A10.94 10.94 0 0 1 12 4c6.5 0 10 7 10 7a18.36 18.36 0 0 1-2.16 3.19M6.61 6.61A18.5 18.5 0 0 0 2 12s3.5 7 10 7a9.86 9.86 0 0 0 4.39-1"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
        </svg>
      )}
    </button>
  );
}

function ChangePasswordModal({ isOpen, onClose, onSuccess }) {
  const { t } = useTranslation();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [status, setStatus] = useState({ type: null, message: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setCurrentPassword("");
      setNewPassword("");
      setShowCurrentPassword(false);
      setShowNewPassword(false);
      setStatus({ type: null, message: "" });
      setIsSubmitting(false);
      return undefined;
    }

    const handleEscape = (event) => {
      if (event.key === "Escape") onClose();
    };

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleEscape);

    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleSubmit = async (event) => {
    event.preventDefault();
    setStatus({ type: null, message: "" });

    if (!currentPassword.trim()) {
      setStatus({ type: "error", message: t("dashboard.changePassword.enterCurrent") });
      return;
    }

    if (!newPassword.trim()) {
      setStatus({ type: "error", message: t("dashboard.changePassword.enterNew") });
      return;
    }

    if (newPassword.length < 8) {
      setStatus({
        type: "error",
        message: t("dashboard.changePassword.minLength"),
      });
      return;
    }

    setIsSubmitting(true);

    try {
      await changePassword({
        current_password: currentPassword,
        new_password: newPassword,
      });
      onSuccess?.();
      onClose();
    } catch (error) {
      setStatus({
        type: "error",
        message:
          error.response?.data?.message ||
          error.message ||
          t("dashboard.changePassword.failed"),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="settings-modal-overlay" onClick={onClose} role="presentation">
      <div
        className="settings-modal"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="change-password-title"
      >
        <div className="settings-modal__header">
          <h2 id="change-password-title" className="settings-modal__title">
            {t("dashboard.changePassword.title")}
          </h2>
          <button
            type="button"
            className="settings-modal__close"
            onClick={onClose}
            aria-label={t("dashboard.common.close")}
          >
            &times;
          </button>
        </div>

        <p className="settings-modal__subtitle">
          {t("dashboard.changePassword.subtitle")}
        </p>

        <form className="settings-modal__form" onSubmit={handleSubmit}>
          <div className="settings-field">
            <label htmlFor="change-password-current">{t("dashboard.changePassword.currentPassword")}</label>
            <div className="settings-field__input-wrap">
              <input
                id="change-password-current"
                type={showCurrentPassword ? "text" : "password"}
                autoComplete="current-password"
                value={currentPassword}
                onChange={(event) => setCurrentPassword(event.target.value)}
                disabled={isSubmitting}
              />
              <PasswordToggle
                visible={showCurrentPassword}
                onToggle={() => setShowCurrentPassword((visible) => !visible)}
                ariaLabel={showCurrentPassword ? t("auth.hidePassword") : t("auth.showPassword")}
              />
            </div>
          </div>

          <div className="settings-field">
            <label htmlFor="change-password-new">{t("dashboard.changePassword.newPassword")}</label>
            <div className="settings-field__input-wrap">
              <input
                id="change-password-new"
                type={showNewPassword ? "text" : "password"}
                autoComplete="new-password"
                value={newPassword}
                onChange={(event) => setNewPassword(event.target.value)}
                disabled={isSubmitting}
              />
              <PasswordToggle
                visible={showNewPassword}
                onToggle={() => setShowNewPassword((visible) => !visible)}
                ariaLabel={showNewPassword ? t("auth.hidePassword") : t("auth.showPassword")}
              />
            </div>
          </div>

          {status.message ? (
            <p
              className={`settings-form-status settings-form-status--${status.type}`}
              role="status"
            >
              {status.message}
            </p>
          ) : null}

          <div className="settings-modal__actions">
            <button
              type="button"
              className="settings-btn settings-btn--outline"
              onClick={onClose}
              disabled={isSubmitting}
            >
              {t("dashboard.common.cancel")}
            </button>
            <button
              type="submit"
              className="settings-btn settings-btn--primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? t("dashboard.common.updating") : t("dashboard.changePassword.update")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ChangePasswordModal;
